import { find, remove } from 'lodash-es'
import { defineStore } from 'pinia'

import { useInstanceCapabilitiesStore } from 'src/stores/instance_capabilities.js'

import { fetchAnyBookmark } from 'src/api/timelines.js'
import {
  createBookmarkFolder,
  deleteBookmarkFolder,
  fetchBookmarkFolders,
  updateBookmarkFolder,
} from 'src/api/user.js'
import { pollingActions } from 'src/services/poller/poller.js'

// the check that runs now, so that menus opened at once share it:
// { id, promise }; the id is also in the store, so that a check from
// before a logout (which resets the store) is neither shared nor kept
let checking = null
let checks = 0

export const useBookmarkFoldersStore = defineStore('bookmarkFolders', {
  state: () => ({
    poller: null,
    allFolders: [],
    // whether anything is bookmarked: the feed menu shows bookmarks only then
    hasBookmarks: false,
    bookmarksChecked: false,
    bookmarksCheck: null,
  }),
  getters: {
    // { emoji, emojiUrl }, nulls when there is none
    findBookmarkFolderEmoji() {
      return (id) => {
        const folder = this.allFolders.find((folder) => folder.id === id)
        return {
          emoji: folder?.emoji ?? null,
          emojiUrl: folder?.emoji_url ?? null,
        }
      }
    },
    findBookmarkFolderName() {
      return (id) => {
        const folder = this.allFolders.find((folder) => folder.id === id)

        if (!folder) return
        return folder.name
      }
    },
  },
  actions: {
    // every four minutes
    ...pollingActions((store) => store.loadBookmarkFolders(), 240000),
    // asks the server once; a failed ask is tried again next time
    checkBookmarks() {
      if (this.bookmarksChecked) return Promise.resolve()
      if (checking && checking.id === this.bookmarksCheck) {
        return checking.promise
      }
      const id = ++checks
      this.bookmarksCheck = id
      const promise = fetchAnyBookmark()
        .then((any) => {
          if (this.bookmarksCheck !== id) return
          this.hasBookmarks ||= any
          this.bookmarksChecked = true
        })
        .catch(() => undefined)
        .finally(() => {
          if (checking?.id === id) checking = null
        })
      checking = { id, promise }
      return promise
    },
    noteBookmark() {
      this.hasBookmarks = true
    },
    loadBookmarkFolders() {
      if (!useInstanceCapabilitiesStore().pleromaBookmarkFoldersAvailable) {
        return Promise.resolve()
      }
      return fetchBookmarkFolders({}).then(({ data: folders }) =>
        this.setBookmarkFolders(folders),
      )
    },
    setBookmarkFolders(value) {
      this.allFolders = value
    },
    setBookmarkFolder({ id, name, emoji, emoji_url: emojiUrl }) {
      const entry = find(this.allFolders, { id })
      if (!entry) {
        this.allFolders.push({ id, name, emoji, emoji_url: emojiUrl })
      } else {
        entry.name = name
        entry.emoji = emoji
        entry.emoji_url = emojiUrl
      }
    },
    createBookmarkFolder({ name, emoji }) {
      return createBookmarkFolder({
        name,
        emoji,
      }).then(({ data: folder }) => {
        this.setBookmarkFolder(folder)
        return folder
      })
    },
    updateBookmarkFolder({ folderId, name, emoji }) {
      return updateBookmarkFolder({
        folderId,
        name,
        emoji,
      }).then(({ data: folder }) => {
        this.setBookmarkFolder(folder)
        return folder
      })
    },
    deleteBookmarkFolder({ folderId }) {
      deleteBookmarkFolder({
        folderId,
      })
      remove(this.allFolders, (folder) => folder.id === folderId)
    },
  },
})
